import { isAppDebugEnabled, logExpandedDebug } from '@/utils/debug/expandedConsoleLog';

const PREFIX = '[module-list-sync]';
const DEBUG_PREFIX = '[MODULE-LIST-DEBUG]';

function countDefectsWithModuleSeq(defects) {
  if (!Array.isArray(defects)) return 0;
  return defects.filter(d => d?.solarModuleSeq != null && d?.solarModuleSeq !== '').length;
}

/**
 * 모듈리스트 ↔ 지도 클릭 디버그 로그 (pi-console-bridge 경유)
 * @param {string} step
 * @param {Record<string, unknown>} [detail]
 */
export function logModuleListSync(step, detail = {}) {
  if (!isAppDebugEnabled()) {
    return;
  }
  logExpandedDebug(PREFIX, step, detail);
}

/**
 * 모듈 리스트 전용 상세 디버그 — 콘솔 필터: [MODULE-LIST-DEBUG]
 * @param {string} event
 * @param {Record<string, unknown>} [detail]
 */
export function logModuleListDebug(event, detail = {}) {
  if (!isAppDebugEnabled()) {
    return;
  }
  logExpandedDebug(DEBUG_PREFIX, event, detail);
}

/** 결함 행의 array/string/module 필드 샘플 */
export function summarizeDefectHierarchySample(defects, limit = 5) {
  if (!isAppDebugEnabled()) {
    return [];
  }
  const list = defects || [];
  return list.slice(0, limit).map(d => ({
    defectSeq: d?.seq,
    detectWay: d?.detectWay ?? null,
    solarModuleSeq: d?.solarModuleSeq ?? null,
    moduleId: d?.moduleId ?? d?.module_id ?? null,
    solarStringSeq: d?.solarStringSeq ?? d?.solar_string_seq ?? null,
    stringId: d?.stringId ?? d?.string_id ?? null,
    solarArraySeq: d?.solarArraySeq ?? d?.solar_array_seq ?? null,
    arrayId: d?.arrayId ?? d?.array_id ?? null
  }));
}

/** siteDesignModuleHierarchy 요약 */
export function summarizeHierarchyTree(hierarchy) {
  if (!isAppDebugEnabled()) {
    return { arrayCount: 0, stringCount: 0, moduleCount: 0, arrays: [] };
  }
  if (!hierarchy?.length) {
    return { arrayCount: 0, stringCount: 0, moduleCount: 0, arrays: [] };
  }
  let stringCount = 0;
  let moduleCount = 0;
  const arrays = hierarchy.map(arrayNode => {
    const strings = (arrayNode.strings || []).map(stringNode => {
      const modules = stringNode.modules || [];
      moduleCount += modules.length;
      return {
        seq: stringNode.seq,
        displayId: stringNode.displayId,
        moduleCount: modules.length,
        modules: modules.slice(0, 4).map(m => ({
          seq: m.seq,
          displayId: m.displayId
        }))
      };
    });
    stringCount += strings.length;
    return {
      seq: arrayNode.seq,
      displayId: arrayNode.displayId,
      stringCount: strings.length,
      strings
    };
  });
  return {
    arrayCount: hierarchy.length,
    stringCount,
    moduleCount,
    arrays
  };
}

/** defectTree 노드 요약 */
export function summarizeDefectTreeNodes(tree) {
  if (!isAppDebugEnabled()) {
    return { arrayCount: 0, stringCount: 0, moduleCount: 0, arrays: [] };
  }
  if (!tree?.length) {
    return { arrayCount: 0, stringCount: 0, moduleCount: 0, arrays: [] };
  }
  let stringCount = 0;
  let moduleCount = 0;
  const arrays = tree.map(arrayNode => {
    const strings = (arrayNode.children || []).map(stringNode => {
      const modules = stringNode.children || [];
      moduleCount += modules.length;
      return {
        seq: stringNode.seq,
        displayId: stringNode.displayId,
        count: stringNode.count,
        modules: modules.slice(0, 4).map(m => ({
          seq: m.seq,
          displayId: m.displayId,
          count: m.count,
          defectSeqs: (m.defects || []).slice(0, 3).map(d => d?.seq)
        }))
      };
    });
    stringCount += strings.length;
    return {
      seq: arrayNode.seq,
      displayId: arrayNode.displayId,
      count: arrayNode.count,
      strings
    };
  });
  return {
    arrayCount: tree.length,
    stringCount,
    moduleCount,
    arrays
  };
}

/** flatRows(virtual scroll) 요약 */
export function summarizeFlatRows(flatRows, limit = 20) {
  if (!isAppDebugEnabled()) {
    return { total: 0, byKind: {}, sample: [] };
  }
  const rows = flatRows || [];
  return {
    total: rows.length,
    byKind: rows.reduce((acc, r) => {
      const k = r?.kind || 'unknown';
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {}),
    sample: rows.slice(0, limit).map(r => ({
      kind: r.kind,
      seq: r.seq,
      displayId: r.displayId,
      arrayDisplayId: r.arrayDisplayId ?? null,
      stringDisplayId: r.stringDisplayId ?? null,
      defectSeq: r.defect?.seq ?? null,
      defectCount: r.defectCount ?? r.defects?.length ?? null
    }))
  };
}

export function summarizeDefectModuleLinkage(defects) {
  if (!isAppDebugEnabled()) {
    return {
      total: 0,
      withSolarModuleSeq: 0,
      withoutSolarModuleSeq: 0,
      sampleWithSeq: [],
      sampleWithoutSeq: []
    };
  }
  const list = defects || [];
  const withSeq = list.filter(d => d?.solarModuleSeq != null && d?.solarModuleSeq !== '');
  return {
    total: list.length,
    withSolarModuleSeq: withSeq.length,
    withoutSolarModuleSeq: list.length - withSeq.length,
    sampleWithSeq: withSeq.slice(0, 3).map(d => ({
      defectSeq: d.seq,
      solarModuleSeq: d.solarModuleSeq,
      moduleId: d.moduleId ?? d.module_id
    })),
    sampleWithoutSeq: list
      .filter(d => d?.solarModuleSeq == null || d?.solarModuleSeq === '')
      .slice(0, 3)
      .map(d => ({ defectSeq: d.seq, moduleId: d.moduleId ?? d.module_id }))
  };
}

/** siteDesign API 계층 — array/string PK가 채워져 있는지 */
export function isCompleteSiteDesignHierarchy(hierarchy) {
  if (!hierarchy?.length) return false;
  return hierarchy.some(
    array =>
      array?.seq != null &&
      array?.strings?.some(stringNode => stringNode?.seq != null)
  );
}

export function countHierarchyModules(hierarchy) {
  let count = 0;
  for (const arrayNode of hierarchy || []) {
    for (const stringNode of arrayNode.strings || []) {
      count += (stringNode.modules || []).length;
    }
  }
  return count;
}

/**
 * enrich API가 만든 hierarchy를 store에 커밋해도 되는지
 * (siteDesign 540모듈 → enrich 4모듈 null seq 덮어쓰기 방지)
 */
export function resolveEnrichHierarchyCommit({
  storeHierarchy,
  candidateHierarchy,
  cachedHierarchy
}) {
  const storeComplete = isCompleteSiteDesignHierarchy(storeHierarchy);
  const candidateComplete = isCompleteSiteDesignHierarchy(candidateHierarchy);
  const cachedComplete = isCompleteSiteDesignHierarchy(cachedHierarchy);
  const storeModules = countHierarchyModules(storeHierarchy);
  const candidateModules = countHierarchyModules(candidateHierarchy);

  if (storeComplete) {
    return {
      commit: false,
      reason: 'store-has-complete-siteDesign',
      storeArrays: storeHierarchy?.length ?? 0,
      storeModules,
      candidateArrays: candidateHierarchy?.length ?? 0,
      candidateModules
    };
  }
  if (cachedComplete && !candidateComplete) {
    return {
      commit: false,
      reason: 'candidate-incomplete-use-cached',
      cachedArrays: cachedHierarchy?.length ?? 0,
      cachedModules: countHierarchyModules(cachedHierarchy)
    };
  }
  if (!candidateHierarchy?.length) {
    return { commit: false, reason: 'candidate-empty' };
  }
  if (candidateComplete || !storeHierarchy?.length) {
    return {
      commit: true,
      reason: candidateComplete ? 'candidate-complete' : 'store-empty-fallback',
      candidateArrays: candidateHierarchy.length,
      candidateModules
    };
  }
  return {
    commit: false,
    reason: 'candidate-would-downgrade-store',
    storeModules,
    candidateModules
  };
}

export function findModuleInSiteDesignHierarchy(hierarchy, moduleSeq) {
  if (!hierarchy?.length || moduleSeq == null) return null;
  const key = String(moduleSeq);
  for (const arrayNode of hierarchy) {
    for (const stringNode of arrayNode.strings || []) {
      const moduleNode = (stringNode.modules || []).find(
        mod => mod?.seq != null && String(mod.seq) === key
      );
      if (moduleNode) {
        return {
          arraySeq: arrayNode.seq,
          arrayDisplayId: arrayNode.displayId,
          stringSeq: stringNode.seq,
          stringDisplayId: stringNode.displayId,
          moduleSeq: moduleNode.seq,
          moduleDisplayId: moduleNode.displayId
        };
      }
    }
  }
  return null;
}

export function findModuleInDefectTree(tree, moduleSeq) {
  if (!tree?.length || moduleSeq == null) return null;
  const key = String(moduleSeq);
  for (const arrayNode of tree) {
    for (const stringNode of arrayNode.children || []) {
      const moduleNode = (stringNode.children || []).find(
        mod => mod?.seq != null && String(mod.seq) === key
      );
      if (moduleNode) {
        return {
          arraySeq: arrayNode.seq,
          stringSeq: stringNode.seq,
          moduleSeq: moduleNode.seq,
          moduleDisplayId: moduleNode.displayId,
          defectCount: moduleNode.count
        };
      }
    }
  }
  return null;
}

export { countDefectsWithModuleSeq };
